import { FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, InlineBanner, Input, notify, Skeleton } from '../../components/ui';
import { useRemote } from '../../features/home/useRemote';
import { useApiError } from '../../hooks/useApiError';
import api from '../../services/api';
import { formatRate } from './format';
import type { FeeRate } from './types';

export interface FeeRateTarget {
    id: string;
    name: string;
}

const MAX_RATE = 20;

/** Whole hundredths only: 1.5 and 1.25 pass, 1.255 does not. */
const validRate = (n: number): boolean => Number.isFinite(n) && n >= 0 && n <= MAX_RATE && Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;

/** Admin only. Shows the rate in force this month and saves a new one, which the API applies from the current month. Mount it only while open. */
export const FeeRateDialog = ({ target, onClose, onSaved }: { target: FeeRateTarget; onClose: () => void; onSaved: () => void }) => {
    const { t, i18n } = useTranslation('billing');
    const { t: tc } = useTranslation();
    const apiError = useApiError();
    const formId = useId();
    const current = useRemote(`fee-rate:${target.id}`, () => api.get<FeeRate>(`/restaurants/${target.id}/fee-rate`).then((r) => r.data));
    const [text, setText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    // Until the user types, the field shows the rate in force.
    const value = text ?? (current.data ? String(current.data.rate) : '');

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        const rate = Number(value);
        if (value.trim() === '' || !validRate(rate)) {
            setError(t('feeRate.invalid', { max: MAX_RATE }));
            return;
        }
        setError(null);
        setBusy(true);
        try {
            await api.put<FeeRate>(`/restaurants/${target.id}/fee-rate`, { rate });
            notify.success(t('feeRate.saved', { name: target.name, rate: formatRate(rate, i18n.language) }));
            onSaved();
            onClose();
        } catch (err) {
            setError(apiError(err));
            setBusy(false);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            busy={busy}
            title={t('feeRate.title', { name: target.name })}
            footer={
                <>
                    <Button variant="outline" onClick={onClose} disabled={busy}>
                        {tc('ui.cancel')}
                    </Button>
                    <Button type="submit" form={formId} loading={busy} disabled={current.state !== 'ready' || !current.data}>
                        {t('feeRate.save')}
                    </Button>
                </>
            }
        >
            {current.state === 'loading' && <Skeleton className="h-24 w-full" />}
            {current.state === 'error' && (
                <InlineBanner tone="danger" action={{ label: tc('ui.retry'), onClick: current.reload }}>
                    {t('feeRate.loadFailed')}
                </InlineBanner>
            )}
            {current.data && (
                <form id={formId} onSubmit={submit} noValidate className="grid gap-4 pb-2">
                    <p className="text-small text-muted">
                        {t('feeRate.current', { rate: formatRate(current.data.rate, i18n.language) })}
                    </p>
                    <Field label={t('feeRate.label')} hint={t('feeRate.hint', { max: MAX_RATE })} error={error ?? undefined}>
                        {(c) => (
                            <Input
                                {...c}
                                type="number"
                                inputMode="decimal"
                                min={0}
                                max={MAX_RATE}
                                step="0.01"
                                value={value}
                                trailing={<span className="pr-3 text-muted" aria-hidden="true">%</span>}
                                onChange={(e) => setText(e.target.value)}
                            />
                        )}
                    </Field>
                    <InlineBanner tone="info">{t('feeRate.applies')}</InlineBanner>
                </form>
            )}
        </Dialog>
    );
};
